import React, {useState, useEffect, useCallback} from 'react'
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import RowLayout from '../../../../components/Layouts/RowLayout';
import {TemplateSchema} from '../../../../api/DBSchema/Template';
import { makeStyles } from '@mui/styles';
import Api from '../../../../api/SMPFetchHelper';
import SMCIFrame from '../../../../components/Simple/SMCIFrame';
import SMCButton from '../../../../components/Simple/SMCButton';
import ReactHtmlParser from 'react-html-parser';
import LoadingContainer from '../../../../components/Page/LoadingContainer';
import {hasValue} from '../../../../utils/utils';

/**
 * @param {Object} params
 * @param {Object} params.templateObject
 * @param {String} params.templateName
 * @param {Boolean} params.previewShortBody
 * @param {'alert'|'template'} params.templateType
 * @param {Boolean} params.isGlobal
 */
function PreviewTab({
    templateObject,
    templateName,
    previewShortBody,
    templateType,
    isGlobal
}) {
    const { t } = useTranslation();
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [sendTo, setSendTo] = useState('');
    const [subjectPreview, setSubjectPreview] = useState('');
    const [bccPreview, setBccPreview] = useState('');
    const [htmlPreview, setHtmlPreview] = useState('');

    const loadPreview = useCallback((templateObject, templateName) => {
        setIsLoading(true);

        const admins = templateObject[TemplateSchema.admins];
        const users = templateObject[TemplateSchema.users];
        const messageData = templateObject[TemplateSchema.messageData];
        const messageDataSchema = TemplateSchema.messageDataSchema;
        const messageSubject = messageData[messageDataSchema.subject];
        const messageTo = messageData[messageDataSchema.to];
        const messageBcc = messageData[messageDataSchema.bcc];
        const messageBody = messageData[messageDataSchema.body];
        const messageShortBody = messageData[messageDataSchema.shortBody];
        
        setSendTo(messageTo);

        const getPreviewApiRequest = templateType === 'alert' ? Api.getAlertPreview : Api.getTemplatePreview;
        getPreviewApiRequest({
            name: templateName,
            isEditor: true,
            bcc: messageBcc,
            subject: messageSubject,
            body: previewShortBody ? messageShortBody : messageBody,
            users: users,
            admins: admins,
            isGlobal: !!isGlobal
        })
        .then(resp => {
            const respData = resp.data.data;
            setSubjectPreview(respData.subject);
            setBccPreview(respData.bcc);
            setHtmlPreview(respData.body);
        })
        .catch(() => {
            setHasError(true);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [previewShortBody, templateType, isGlobal]);

    useEffect(() => {
        loadPreview(templateObject, templateName);
    }, [templateName, templateObject, loadPreview]);
    
    return (
        <div style={{ width: '100%' }}>
            <RowLayout>
                <SMCLabel width={70}>{t('SMC_MESSEGE_SUBJECT')}</SMCLabel>
                <SMCLabel>{subjectPreview}</SMCLabel>
            </RowLayout>
            {
                hasValue(sendTo) && (
                    <RowLayout>
                        <SMCLabel width={70}>{t('SMC_MESSEGE_TO')}</SMCLabel>
                        <SMCLabel>{sendTo}</SMCLabel>
                    </RowLayout>
                )
            }
            <RowLayout>
                <SMCLabel width={70}>{t('SMC_MESSEGE_BCC')}</SMCLabel>
                <SMCLabel>{Array.isArray(bccPreview) ? bccPreview.join(';') : bccPreview}</SMCLabel>
            </RowLayout>
            <SMCIFrame title="previewBox" className={classes.messagePreviewBox}>
                <LoadingContainer loading={isLoading}>
                    {
                        hasError && (
                            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <SMCLabel bold>Error loading preview</SMCLabel><br />
                                <SMCButton onClick={() => loadPreview(templateObject, templateName)} style={{ height: 20 }}>Try again</SMCButton>
                            </div>
                        )
                    }
                    {
                        (!hasError && htmlPreview) && (
                            ReactHtmlParser(htmlPreview)
                        )
                    }
                </LoadingContainer>
            </SMCIFrame>
        </div>
    );
}

const useStyles = makeStyles({
    messagePreviewBox: {
        height: 300,
        width: 680
    }
});

export default React.memo(PreviewTab);